import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import projectsService from "../services/projectsService";
import {
  readCollection,
  readProjectList,
  projectListView,
} from "../features/journey/journeyPresentation";
import {
  ProjectsRefused,
  ProjectsEmpty,
  UnreadableRowsNotice,
} from "../features/journey/ProjectListStates";
import contactsService from "../services/contactsService";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Plus,
  Search,
  Building2,
  MapPin,
  MoreVertical,
  Trash2,
  Eye,
  Loader2,
  X,
  UserCircle,
  MessageSquare,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

// Property classifications and project kinds
const PROPERTY_CLASSES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
];

const PROJECT_KINDS = [
  { value: "new_build", label: "New Build" },
  { value: "remodel", label: "Remodel" },
  { value: "repair", label: "Repair" },
  { value: "tenant_improvement", label: "Tenant Improvement" },
  { value: "service_call", label: "Service Call" },
  { value: "other", label: "Other" },
];

// US States for dropdown
const US_STATES = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" }, { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" }, { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" }, { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" }, { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" }, { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" }, { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" }, { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" }, { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" }, { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" }, { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" }, { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" }, { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" }, { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" }, { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" }, { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" }, { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" }, { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" }, { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" }, { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" }, { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" }, { value: "WY", label: "Wyoming" },
];

const ProjectsPage = () => {
  /**
   * The list is held as a READ, not as an array.
   *
   * `useState([])` was the defect: a failed request left it [] and the page
   * announced "No projects found — Get started by creating your first
   * project" to a contractor who might have forty. A read carries whether it
   * happened, so the screen can tell an empty organisation from a failed
   * request from a payload it cannot parse.
   */
  const [projectsRead, setProjectsRead] = useState(null);
  const projects = projectsRead && projectsRead.state === "READ" ? projectsRead.items : [];
  const [workTypes, setWorkTypes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactTypes, setContactTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  // Contact selection mode: "existing" or "new"
  const [contactMode, setContactMode] = useState("existing");
  const [selectedContactId, setSelectedContactId] = useState("");
  const [newContact, setNewContact] = useState({
    contact_type: "client",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    title: "",
  });

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    property_class: "commercial",
    project_kind: "new_build",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    postal_code: "",
    country_code: "US",
    work_type_codes: [],
    primary_work_type: "",
    budget: "",
    client_name: "",
  });

  useEffect(() => {
    fetchProjects();
    fetchWorkTypes();
    fetchContacts();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectsService.getAll();
      setProjectsRead(readProjectList(data));
    } catch (error) {
      // A toast disappears. The screen has to keep saying that this list was
      // not read, because the alternative is an empty list that reads as a
      // statement about the business.
      setProjectsRead(readProjectList(null, { requestFailed: true }));
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkTypes = async () => {
    try {
      const data = await projectsService.getWorkTypes();
      // `workTypes.map` renders the create-project form. A payload that is not
      // a list used to throw straight through render and take the whole
      // project list down with it — the error boundary caught it and showed
      // "This screen failed to render", which is the page saying nothing at
      // all rather than saying what it knows.
      setWorkTypes(readCollection(data).items);
    } catch (error) {
      console.error("Failed to load work types:", error);
    }
  };

  const fetchContacts = async () => {
    try {
      const [contactsData, typesData] = await Promise.all([
        contactsService.getAll(),
        contactsService.getContactTypes(),
      ]);
      // Same rule as workTypes: both are mapped during render, so a payload
      // that is not a list must not reach them.
      setContacts(readCollection(contactsData).items);
      setContactTypes(readCollection(typesData).items);
    } catch (error) {
      console.error("Failed to load contacts:", error);
    }
  };

  const openProjectChannel = async (projectId) => {
    try {
      const response = await axios.post(`${API}/messages/bootstrap/project/${projectId}`);
      navigate(`/messages?conversation=${response.data.id}`);
    } catch {
      toast.error("Failed to open project channel");
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.address_line1 || !formData.city || !formData.state || !formData.postal_code) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    if (formData.work_type_codes.length === 0) {
      toast.error("Please select at least one work type");
      return;
    }

    // Validate contact if creating new
    if (contactMode === "new" && (!newContact.first_name || !newContact.last_name)) {
      toast.error("Please provide contact first and last name");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        primary_work_type: formData.primary_work_type || formData.work_type_codes[0],
      };
      
      // Create the project
      const newProject = await projectsService.create(payload);
      
      // Handle contact association
      let contactId = selectedContactId;
      
      if (contactMode === "new" && newContact.first_name && newContact.last_name) {
        // Create new contact first
        const createdContact = await contactsService.create(newContact);
        contactId = createdContact.id;
        setContacts(prev => [createdContact, ...prev]);
      }
      
      // Associate contact with project if selected
      if (contactId) {
        await contactsService.addToProject(newProject.id, {
          contact_id: contactId,
          role: "Primary Contact",
          is_primary: true,
        });
      }
      
      setProjectsRead((prev) =>
        prev && prev.state === "READ"
          ? { ...prev, items: [newProject, ...prev.items] }
          : readProjectList([newProject]),
      );
      toast.success("Project created successfully!");
      setIsCreateOpen(false);
      resetForm();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create project";
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      property_class: "commercial",
      project_kind: "new_build",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      postal_code: "",
      country_code: "US",
      work_type_codes: [],
      primary_work_type: "",
      budget: "",
      client_name: "",
    });
    setContactMode("existing");
    setSelectedContactId("");
    setNewContact({
      contact_type: "client",
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      company: "",
      title: "",
    });
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;
    try {
      await projectsService.delete(projectId);
      setProjectsRead((prev) =>
        prev && prev.state === "READ"
          ? { ...prev, items: prev.items.filter((p) => p.id !== projectId) }
          : prev,
      );
      toast.success("Project deleted");
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  const toggleWorkType = (code) => {
    setFormData((prev) => {
      const codes = prev.work_type_codes.includes(code)
        ? prev.work_type_codes.filter((c) => c !== code)
        : [...prev.work_type_codes, code];
      
      // Reset primary if it's being removed
      const primary = codes.includes(prev.primary_work_type) ? prev.primary_work_type : "";
      
      return { ...prev, work_type_codes: codes, primary_work_type: primary };
    });
  };

  const listView = projectListView(projectsRead);

  const filteredProjects = projects.filter((project) => {
    const matchesSearch =
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.project_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.formatted_address?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || project.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const variants = {
      proposed: "bg-blue-500/20 text-blue-400",
      active: "bg-emerald-500/20 text-emerald-400",
      on_hold: "bg-amber-500/20 text-amber-400",
      complete: "bg-green-500/20 text-green-400",
      canceled: "bg-red-500/20 text-red-400",
    };
    return variants[status] || "bg-muted text-muted-foreground";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">Projects</h1>
          <p className="text-muted-foreground mt-1">
            Manage and track all your construction projects
          </p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 glow-primary">
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[700px] bg-card border-border max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Create New Project</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateProject} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="name">Project Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Downtown Office Building"
                      required
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>Property Class *</Label>
                    <Select
                      value={formData.property_class}
                      onValueChange={(v) => setFormData({ ...formData, property_class: v })}
                    >
                      <SelectTrigger className="bg-muted/50 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_CLASSES.map((pc) => (
                          <SelectItem key={pc.value} value={pc.value}>{pc.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Project Type *</Label>
                    <Select
                      value={formData.project_kind}
                      onValueChange={(v) => setFormData({ ...formData, project_kind: v })}
                    >
                      <SelectTrigger className="bg-muted/50 mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROJECT_KINDS.map((pk) => (
                          <SelectItem key={pk.value} value={pk.value}>{pk.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">Project Address</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="address_line1">Street Address *</Label>
                    <Input
                      id="address_line1"
                      value={formData.address_line1}
                      onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                      placeholder="123 Main Street"
                      required
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="address_line2">Unit / Suite (Optional)</Label>
                    <Input
                      id="address_line2"
                      value={formData.address_line2}
                      onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                      placeholder="Suite 100"
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Los Angeles"
                      required
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(v) => setFormData({ ...formData, state: v })}
                    >
                      <SelectTrigger className="bg-muted/50 mt-1">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="postal_code">ZIP Code *</Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                      placeholder="90012"
                      required
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="budget">Budget ($)</Label>
                    <Input
                      id="budget"
                      type="number"
                      value={formData.budget}
                      onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                      placeholder="1000000"
                      className="bg-muted/50 mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Work Types */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">Work Types *</h3>
                <p className="text-xs text-muted-foreground">Select all that apply. Click again to set as primary.</p>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg bg-muted/30">
                  {workTypes.map((wt) => {
                    const isSelected = formData.work_type_codes.includes(wt.code);
                    const isPrimary = formData.primary_work_type === wt.code;
                    return (
                      <Badge
                        key={wt.code}
                        variant={isSelected ? "default" : "outline"}
                        className={`cursor-pointer transition-all ${
                          isPrimary ? "ring-2 ring-primary" : ""
                        } ${isSelected ? "bg-primary/80 hover:bg-primary" : "hover:bg-muted"}`}
                        onClick={() => {
                          if (isSelected && !isPrimary) {
                            setFormData({ ...formData, primary_work_type: wt.code });
                          } else {
                            toggleWorkType(wt.code);
                          }
                        }}
                      >
                        {wt.name}
                        {isPrimary && <span className="ml-1 text-xs">(Primary)</span>}
                      </Badge>
                    );
                  })}
                </div>
                {formData.work_type_codes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    <span className="text-xs text-muted-foreground">Selected:</span>
                    {formData.work_type_codes.map((code) => (
                      <Badge key={code} variant="secondary" className="text-xs">
                        {workTypes.find((w) => w.code === code)?.name}
                        <X
                          className="ml-1 h-3 w-3 cursor-pointer"
                          onClick={() => toggleWorkType(code)}
                        />
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Primary Contact */}
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">Primary Contact</h3>
                <Tabs value={contactMode} onValueChange={setContactMode} className="w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-muted/50">
                    <TabsTrigger value="existing">Select Existing</TabsTrigger>
                    <TabsTrigger value="new">Create New</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="existing" className="mt-4">
                    <Select value={selectedContactId || "none"} onValueChange={(v) => setSelectedContactId(v === "none" ? "" : v)}>
                      <SelectTrigger className="bg-muted/50">
                        <SelectValue placeholder="Select a contact (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No contact</SelectItem>
                        {contacts.map((contact) => (
                          <SelectItem key={contact.id} value={contact.id}>
                            <div className="flex items-center gap-2">
                              <UserCircle className="h-4 w-4" />
                              {contact.full_name}
                              {contact.company && <span className="text-muted-foreground">({contact.company})</span>}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {contacts.length === 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        No contacts available. Switch to "Create New" to add one.
                      </p>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="new" className="mt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Type</Label>
                        <Select
                          value={newContact.contact_type}
                          onValueChange={(v) => setNewContact({ ...newContact, contact_type: v })}
                        >
                          <SelectTrigger className="bg-muted/50 mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {contactTypes.map((ct) => (
                              <SelectItem key={ct.value} value={ct.value}>{ct.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="contact_company">Company</Label>
                        <Input
                          id="contact_company"
                          value={newContact.company}
                          onChange={(e) => setNewContact({ ...newContact, company: e.target.value })}
                          placeholder="Company name"
                          className="bg-muted/50 mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_first">First Name *</Label>
                        <Input
                          id="contact_first"
                          value={newContact.first_name}
                          onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                          placeholder="First name"
                          className="bg-muted/50 mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_last">Last Name *</Label>
                        <Input
                          id="contact_last"
                          value={newContact.last_name}
                          onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                          placeholder="Last name"
                          className="bg-muted/50 mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_email">Email</Label>
                        <Input
                          id="contact_email"
                          type="email"
                          value={newContact.email}
                          onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                          placeholder="email@example.com"
                          className="bg-muted/50 mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact_phone">Phone</Label>
                        <Input
                          id="contact_phone"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="bg-muted/50 mt-1"
                        />
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating} className="bg-primary hover:bg-primary/90">
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Project"
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-muted/50"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-muted/50">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="proposed">Proposed</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="on_hold">On Hold</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="canceled">Canceled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/*
        Rows the backend sent that this list cannot render are counted BESIDE
        the ones it can, never instead of them. A silent shortfall is how "12
        projects" becomes the truth when the backend sent 13.
      */}
      <UnreadableRowsNotice read={projectsRead} />

      {/* Projects Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card
              key={project.id}
              className="stat-card hover:border-primary/30 transition-all duration-200"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold font-heading line-clamp-1">{project.name}</h3>
                      <p className="text-xs text-muted-foreground font-mono">{project.project_code}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/projects/${project.id}`)}>
                        <Eye className="mr-2 h-4 w-4" />
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openProjectChannel(project.id)}>
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Open Project Channel
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDeleteProject(project.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-4 space-y-3">
                  {/* Address */}
                  {project.formatted_address && (
                    <div className="flex items-start gap-2 text-sm text-muted-foreground">
                      <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                      <span className="line-clamp-2">{project.formatted_address}</span>
                    </div>
                  )}

                  {/* Status & Type */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs px-2 py-1 rounded capitalize ${getStatusBadge(project.status)}`}>
                      {project.status?.replace("_", " ")}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-muted/50 text-muted-foreground capitalize">
                      {project.property_class}
                    </span>
                    <span className="text-xs px-2 py-1 rounded bg-muted/50 text-muted-foreground capitalize">
                      {project.project_kind?.replace("_", " ")}
                    </span>
                  </div>

                  {/* Work Types */}
                  {project.work_types?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {project.work_types.slice(0, 3).map((wt) => (
                        <Badge key={wt.code} variant="outline" className="text-xs">
                          {wt.name}
                        </Badge>
                      ))}
                      {project.work_types.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{project.work_types.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Budget */}
                  {project.budget && (
                    <p className="text-sm font-mono text-muted-foreground">
                      ${project.budget.toLocaleString()}
                    </p>
                  )}
                </div>

                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => navigate(`/projects/${project.id}`)}
                >
                  View Project
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : listView.kind === "REFUSED" || listView.kind === "UNREADABLE" ? (
        /*
          The request failed, or the payload was not a list.

          Neither is a fact about this organisation, so neither renders as one:
          no "No projects found", and no invitation to create a first project.
        */
        <ProjectsRefused
          view={listView}
          onRetry={fetchProjects}
          testId={listView.kind === "REFUSED" ? "projects-refused" : "projects-unreadable"}
        />
      ) : listView.kind === "EMPTY" ? (
        <ProjectsEmpty view={listView} onCreate={() => setIsCreateOpen(true)} />
      ) : (
        /*
          Rows exist, but this search or filter matched none of them. That is a
          statement about the FILTER, not about the organisation, so it never
          offers to create a first project.
        */
        <Card className="stat-card" data-testid="projects-no-match">
          <CardContent className="p-12 text-center" role="status" aria-live="polite">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <h2 className="text-lg font-semibold mb-2">No project matches this search</h2>
            <p className="text-muted-foreground mb-4">
              {listView.projects.length === 1
                ? "1 project was read. Adjust the search or filters to see it."
                : `${listView.projects.length} projects were read. Adjust the search or filters to see them.`}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProjectsPage;
